import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, downloadArtifactPdf } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isViewer } from "@/lib/guards";
import { pdfFallbackName } from "@/lib/tables";
import AppHeader from "@/components/AppHeader";
import Markdown from "@/components/Markdown";
import CarouselArtifact from "@/components/agent/CarouselArtifact";
import { ListSkeleton, ErrorState } from "@/components/shared/StateViews";
import { friendlyError } from "@/lib/errors";
import {
  BookMarked, Trash2, Download, Loader2, CalendarPlus, Instagram, Share2, Copy,
  Maximize2, ArrowUpDown, ArrowRight, LayoutGrid, Table2,
} from "lucide-react";
import ArtifactStage from "@/components/shared/ArtifactStage";
import TablesBar from "@/components/shared/TablesBar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AddToCalendarSheet from "@/components/calendar/AddToCalendarSheet";
import { extractInstagramCaption, hasInstagramCaption } from "@/lib/caption";
import {
  TYPE_LABEL, TYPE_FILTERS, STATUS_LABEL, agentLabel, continueAgentPath,
  filterLibraryItems, sortLibraryItems, agentsInItems,
  readLibraryView, writeLibraryView,
} from "@/lib/library";
import { toast } from "sonner";

const canWebShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

const chip = (active) =>
  `shrink-0 px-4 py-2 rounded-full text-[12px] font-ui tracking-wide transition-colors min-h-[40px] ${
    active
      ? "bg-brand-rose text-white"
      : "bg-brand-surface text-brand-ink2 border border-brand-line hover:border-brand-rose"
  }`;

export default function ContentLibrary() {
  const { user } = useAuth();
  const viewer = isViewer(user);
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [agentsBySlug, setAgentsBySlug] = useState({});
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") === "draft" ? "draft" : "saved");
  const [typeFilter, setTypeFilter] = useState("all");
  const [agentFilter, setAgentFilter] = useState("all");
  const [view, setView] = useState(() => readLibraryView());
  const [sortKey, setSortKey] = useState("updated_at");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfId, setPdfId] = useState(null);
  const [calArtifact, setCalArtifact] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [stageOpen, setStageOpen] = useState(false);

  const downloadPdf = async (item) => {
    if (pdfId) return;
    setPdfId(item.id);
    try {
      await downloadArtifactPdf(item.id, pdfFallbackName(item));
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setPdfId(null);
    }
  };

  const copyArtifact = async (item) => {
    try {
      await navigator.clipboard.writeText(item.content);
      toast.success("Conteúdo copiado.");
    } catch {
      toast.error("Não consegui copiar. Tente novamente.");
    }
  };

  const copyForInstagram = async (item) => {
    const caption = extractInstagramCaption(item.content);
    if (!caption) {
      toast.error("Este conteúdo não tem uma legenda pronta. Use Copiar para o roteiro.");
      return;
    }
    try {
      await navigator.clipboard.writeText(caption);
      toast.success("Legenda pronta pro Instagram ✦ É só colar.");
    } catch {
      toast.error("Não consegui copiar. Tente novamente.");
    }
  };

  const shareForInstagram = async (item) => {
    const caption = extractInstagramCaption(item.content) || item.content;
    try {
      await navigator.share({ title: item.title || "Conteúdo", text: caption });
    } catch {
      /* user dismissed the native share sheet — no-op */
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [savedRes, draftRes, agentsRes] = await Promise.all([
        api.get("/artifacts", { params: { status: "saved" } }),
        api.get("/artifacts", { params: { status: "draft" } }),
        api.get("/agents"),
      ]);
      const list = [...(savedRes.data || []), ...(draftRes.data || [])];
      const bySlug = {};
      for (const a of agentsRes.data || []) bySlug[a.slug] = a;
      setAgentsBySlug(bySlug);
      setItems(list);
      if (list.length) {
        const wanted = searchParams.get("artifact");
        const match = wanted ? list.find((i) => i.id === wanted) : null;
        if (match) {
          if (match.status === "draft" || match.status === "saved") setStatusFilter(match.status);
          setSelected(match);
        }
      }
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    const filtered = filterLibraryItems(items, { status: statusFilter, type: typeFilter, agent: agentFilter });
    return sortLibraryItems(filtered, { key: sortKey, dir: sortDir }, agentsBySlug);
  }, [items, statusFilter, typeFilter, agentFilter, sortKey, sortDir, agentsBySlug]);

  const agentOptions = useMemo(
    () => agentsInItems(filterLibraryItems(items, { status: statusFilter, type: "all", agent: "all" }), agentsBySlug),
    [items, statusFilter, agentsBySlug],
  );

  const draftCount = useMemo(() => items.filter((i) => i.status === "draft").length, [items]);

  useEffect(() => {
    if (!visible.length) {
      setSelected(null);
      return;
    }
    if (!selected || !visible.some((i) => i.id === selected.id)) setSelected(visible[0]);
  }, [visible, selected]);

  const confirmRemove = async () => {
    const id = deleteTarget;
    setDeleteTarget(null);
    if (!id) return;
    try {
      await api.delete(`/artifacts/${id}`);
      const next = items.filter((i) => i.id !== id);
      setItems(next);
      toast.success("Artefato apagado.");
    } catch (err) {
      toast.error(friendlyError(err));
    }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(key === "updated_at" ? "desc" : "asc");
    }
  };

  const setLibraryView = (next) => {
    setView(next);
    writeLibraryView(next);
  };

  const openRow = (it) => {
    setSelected(it);
    if (view === "table") setStageOpen(true);
  };

  const continueTo = (it, e) => {
    e?.stopPropagation();
    const path = continueAgentPath(it);
    if (path) nav(path);
  };

  return (
    <div className="min-h-screen bg-brand-bg starfield relative" data-testid="content-library-page">
      <AppHeader />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-10 pb-28 md:pb-10">
        <div className="mb-8">
          <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-ui mb-2">
            Biblioteca de Conteúdo
          </div>
          <h1 className="font-display text-[2rem] leading-tight sm:text-4xl lg:text-5xl text-brand-ink">
            {statusFilter === "draft" ? "Rascunhos" : "Seus artefatos salvos"}
          </h1>
        </div>

        <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-soft pb-1" data-testid="library-status-tabs">
          <button type="button" data-testid="library-status-saved" onClick={() => setStatusFilter("saved")} className={chip(statusFilter === "saved")}>
            Salvos
          </button>
          <button type="button" data-testid="library-status-draft" onClick={() => setStatusFilter("draft")} className={chip(statusFilter === "draft")}>
            Rascunhos{draftCount ? ` · ${draftCount}` : ""}
          </button>
        </div>

        <div className="flex flex-col gap-2 mb-6">
          <div className="flex gap-2 overflow-x-auto scrollbar-soft pb-1 -mx-1 px-1">
            {TYPE_FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                data-testid={`filter-${f}`}
                onClick={() => setTypeFilter(f)}
                className={chip(typeFilter === f)}
              >
                {f === "all" ? "Todos" : TYPE_LABEL[f]}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="library-agent-filter">Filtrar por agente</label>
            <select
              id="library-agent-filter"
              data-testid="library-agent-filter"
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              className="min-w-[7rem] flex-1 sm:flex-none h-10 px-3 rounded-full bg-brand-surface text-brand-ink2 border border-brand-line text-[12px] font-ui tracking-wide hover:border-brand-rose min-h-[44px]"
            >
              <option value="all">Agente</option>
              {agentOptions.map((a) => (
                <option key={a.slug} value={a.slug}>{a.nome}</option>
              ))}
            </select>
            {view === "cards" && (
              <select
                data-testid="library-sort"
                value={`${sortKey}:${sortDir}`}
                onChange={(e) => {
                  const [k, d] = e.target.value.split(":");
                  setSortKey(k);
                  setSortDir(d);
                }}
                className="min-w-[7rem] flex-1 sm:flex-none h-10 px-3 rounded-full bg-brand-surface text-brand-ink2 border border-brand-line text-[12px] font-ui tracking-wide hover:border-brand-rose min-h-[44px]"
              >
                <option value="updated_at:desc">Recentes</option>
                <option value="updated_at:asc">Mais antigos</option>
                <option value="title:asc">Título A–Z</option>
                <option value="title:desc">Título Z–A</option>
                <option value="agent:asc">Agente A–Z</option>
                <option value="type:asc">Tipo</option>
              </select>
            )}
            <div className="flex rounded-full border border-brand-line bg-brand-surface p-0.5 shrink-0 ml-auto" data-testid="library-view-toggle">
              <button
                type="button"
                data-testid="library-view-cards"
                onClick={() => setLibraryView("cards")}
                className={`p-2 rounded-full min-h-[44px] min-w-[44px] inline-flex items-center justify-center ${view === "cards" ? "bg-brand-rose text-white" : "text-brand-ink2 hover:text-brand-ink"}`}
                title="Cards"
                aria-label="Vista em cards"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                type="button"
                data-testid="library-view-table"
                onClick={() => setLibraryView("table")}
                className={`p-2 rounded-full min-h-[44px] min-w-[44px] inline-flex items-center justify-center ${view === "table" ? "bg-brand-rose text-white" : "text-brand-ink2 hover:text-brand-ink"}`}
                title="Tabela"
                aria-label="Vista em tabela"
              >
                <Table2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <ListSkeleton count={4} />
        ) : error ? (
          <ErrorState message={error} onRetry={load} testid="library-error" />
        ) : visible.length === 0 ? (
          <div className="bg-brand-surface border border-dashed border-brand-line rounded-3xl p-8 sm:p-14 text-center" data-testid="library-empty">
            <BookMarked className="w-8 h-8 text-brand-rose mx-auto mb-4" />
            <h3 className="font-display text-2xl text-brand-ink mb-2">
              {statusFilter === "draft" ? "Nenhum rascunho agora" : "Sua biblioteca vai crescer aqui"}
            </h3>
            <p className="text-[14px] text-brand-ink2 max-w-md mx-auto">
              {statusFilter === "draft"
                ? "Quando você gerar um conteúdo e ainda não salvar, ele aparece aqui pra continuar no agente."
                : "Cada roteiro ou conteúdo que você confirmar com um agente é salvo aqui automaticamente."}
            </p>
          </div>
        ) : view === "table" ? (
          <LibraryTable
            rows={visible}
            selected={selected}
            agentsBySlug={agentsBySlug}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={toggleSort}
            onOpen={openRow}
            onContinue={continueTo}
          />
        ) : (
          <div className="grid lg:grid-cols-[0.9fr_1.4fr] gap-5 [&>*]:min-w-0">
            <div className="space-y-2.5">
              {visible.map((it) => (
                <LibraryCard
                  key={it.id}
                  it={it}
                  selected={selected?.id === it.id}
                  agentsBySlug={agentsBySlug}
                  onSelect={() => {
                    setSelected(it);
                    if (typeof window !== "undefined" && !window.matchMedia("(min-width: 1024px)").matches) {
                      setStageOpen(true);
                    }
                  }}
                  onContinue={continueTo}
                  onCal={!viewer ? () => setCalArtifact(it) : null}
                  onPdf={() => downloadPdf(it)}
                  pdfLoading={pdfId === it.id}
                />
              ))}
            </div>
            {selected && (
              <div className="hidden lg:block min-w-0">
                <LibraryDetail
                  selected={selected}
                  agentsBySlug={agentsBySlug}
                  viewer={viewer}
                  pdfId={pdfId}
                  onExpand={() => setStageOpen(true)}
                  onCopy={() => copyArtifact(selected)}
                  onPdf={() => downloadPdf(selected)}
                  onInstagram={() => copyForInstagram(selected)}
                  onShare={() => shareForInstagram(selected)}
                  onDelete={() => setDeleteTarget(selected.id)}
                  onContinue={continueTo}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <AddToCalendarSheet
        open={Boolean(calArtifact)}
        onOpenChange={(o) => !o && setCalArtifact(null)}
        preselectedArtifact={calArtifact}
      />

      <ArtifactStage
        open={stageOpen && Boolean(selected)}
        onClose={() => setStageOpen(false)}
        artifact={selected ? { ...selected, status: selected.status || "saved" } : selected}
        viewer={viewer}
        onCopy={() => copyArtifact(selected)}
        onInstagram={() => copyForInstagram(selected)}
        onShare={() => shareForInstagram(selected)}
        onPdf={() => downloadPdf(selected)}
        pdfLoading={pdfId === selected?.id}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="library-delete-dialog" className="bg-brand-surface border-brand-line">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-brand-ink">Apagar este artefato?</AlertDialogTitle>
            <AlertDialogDescription className="text-brand-ink2">
              Esta ação não pode ser desfeita. Entradas do calendário ligadas a ele
              perdem o vínculo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-brand-elevated border-brand-line text-brand-ink2">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="library-delete-confirm"
              onClick={confirmRemove}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Apagar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AgentTypeLine({ it, agentsBySlug }) {
  const type = TYPE_LABEL[it.type] || it.type;
  const agent = agentLabel(it.agent_slug, agentsBySlug);
  return (
    <div className="text-[10.5px] font-ui tracking-[0.2em] uppercase text-brand-rose mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid={`library-agent-tag-${it.id}`}>
      <span>{type}</span>
      {it.agent_slug && (
        <>
          <span className="text-brand-ink2/50">·</span>
          <span className="text-brand-ink2 tracking-[0.14em]">{agent}</span>
        </>
      )}
      {it.status === "draft" && (
        <span className="normal-case tracking-normal px-1.5 py-0.5 rounded-full bg-brand-gold/15 text-brand-gold text-[10px]">
          Rascunho
        </span>
      )}
    </div>
  );
}

function ContinueBtn({ it, onContinue, compact, iconOnly }) {
  if (!continueAgentPath(it)) return null;
  return (
    <button
      type="button"
      data-testid={`library-continue-${it.id}`}
      onClick={(e) => onContinue(it, e)}
      aria-label="Continuar no agente"
      title="Continuar no agente"
      className={iconOnly
        ? "inline-flex items-center justify-center w-11 h-11 rounded-full text-brand-rose hover:bg-brand-elevated"
        : compact
          ? "inline-flex items-center gap-1 text-[11px] font-ui text-brand-rose hover:underline min-h-[32px] whitespace-nowrap"
          : "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-brand-elevated border border-brand-line text-[12.5px] font-ui text-brand-ink hover:border-brand-rose hover:text-brand-rose transition-colors min-h-[40px]"}
    >
      {iconOnly ? <ArrowRight className="w-4 h-4" /> : <>Continuar no agente <ArrowRight className="w-3.5 h-3.5" /></>}
    </button>
  );
}

function LibraryCard({ it, selected, agentsBySlug, onSelect, onContinue, onCal, onPdf, pdfLoading }) {
  return (
    <div className="relative">
      <button
        type="button"
        data-testid={`library-item-${it.id}`}
        onClick={onSelect}
        className={`w-full text-left bg-brand-surface border rounded-2xl p-4 pr-16 transition-colors ${
          it.status === "draft" ? "pb-10" : ""
        } ${
          selected ? "border-brand-rose" : "border-brand-line hover:border-brand-rose/60"
        }`}
      >
        <AgentTypeLine it={it} agentsBySlug={agentsBySlug} />
        <div className="font-display text-lg text-brand-ink mb-1 leading-tight">
          {it.title}
        </div>
        <div className="text-[12px] text-brand-ink2 font-ui">
          {new Date(it.updated_at).toLocaleDateString("pt-BR")}
        </div>
      </button>
      {it.status === "draft" && (
        <div className="absolute bottom-3 left-4">
          <ContinueBtn it={it} onContinue={onContinue} compact />
        </div>
      )}
      <div className="absolute top-3 right-3 flex flex-col gap-1.5">
        {onCal && it.status === "saved" && (
          <button
            type="button"
            data-testid={`library-add-calendar-${it.id}`}
            onClick={onCal}
            aria-label="Adicionar ao calendário"
            title="Adicionar ao calendário"
            className="w-11 h-11 flex items-center justify-center rounded-full bg-brand-elevated border border-brand-line text-brand-ink2 hover:text-brand-rose hover:border-brand-rose transition-colors"
          >
            <CalendarPlus className="w-4 h-4" />
          </button>
        )}
        {it.status === "saved" && (
          <button
            type="button"
            data-testid={`library-pdf-${it.id}`}
            onClick={onPdf}
            disabled={pdfLoading}
            aria-label="Baixar PDF"
            title="Baixar PDF"
            className="w-11 h-11 flex items-center justify-center rounded-full bg-brand-elevated border border-brand-line text-brand-ink2 hover:text-brand-rose hover:border-brand-rose transition-colors disabled:opacity-50"
          >
            {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
}

function SortHead({ id, label, sortKey, sortDir, onSort }) {
  const active = sortKey === id;
  return (
    <button
      type="button"
      data-testid={`library-col-${id}`}
      onClick={() => onSort(id)}
      className="inline-flex items-center gap-1 font-ui text-[11px] tracking-[0.16em] uppercase text-brand-ink2 hover:text-brand-ink"
    >
      {label}
      <ArrowUpDown className={`w-3 h-3 ${active ? "text-brand-rose" : "opacity-40"}`} />
      {active && <span className="sr-only">{sortDir === "asc" ? "crescente" : "decrescente"}</span>}
    </button>
  );
}

function LibraryTable({ rows, selected, agentsBySlug, sortKey, sortDir, onSort, onOpen, onContinue }) {
  return (
    <div className="bg-brand-surface border border-brand-line rounded-3xl overflow-x-auto scrollbar-soft" data-testid="library-table">
      <table className="w-full text-left table-fixed md:table-auto">
        <thead>
          <tr className="border-b border-brand-line">
            <th className="px-4 sm:px-5 py-3"><SortHead id="title" label="Título" sortKey={sortKey} sortDir={sortDir} onSort={onSort} /></th>
            <th className="hidden md:table-cell px-3 py-3"><SortHead id="type" label="Tipo" sortKey={sortKey} sortDir={sortDir} onSort={onSort} /></th>
            <th className="hidden sm:table-cell px-2 sm:px-3 py-3"><SortHead id="agent" label="Agente" sortKey={sortKey} sortDir={sortDir} onSort={onSort} /></th>
            <th className="hidden lg:table-cell px-3 py-3"><SortHead id="status" label="Status" sortKey={sortKey} sortDir={sortDir} onSort={onSort} /></th>
            <th className="hidden sm:table-cell px-3 py-3"><SortHead id="updated_at" label="Data" sortKey={sortKey} sortDir={sortDir} onSort={onSort} /></th>
            <th className="px-0 sm:px-5 py-3 w-14 sm:w-auto text-right font-ui text-[11px] tracking-[0.16em] uppercase text-brand-ink2">
              <span className="hidden md:inline">Ação</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((it) => (
            <tr
              key={it.id}
              data-testid={`library-row-${it.id}`}
              onClick={() => onOpen(it)}
              className={`cursor-pointer border-b border-brand-line/60 last:border-0 hover:bg-brand-elevated/60 ${selected?.id === it.id ? "bg-brand-elevated/40" : ""}`}
            >
              <td className="px-4 sm:px-5 py-3.5 font-display text-[15px] sm:text-[16px] text-brand-ink">
                <span className="line-clamp-2 break-words">{it.title}</span>
                <span className="sm:hidden block mt-1 text-[11px] text-brand-ink2 font-ui font-normal tracking-normal">
                  {new Date(it.updated_at).toLocaleDateString("pt-BR")}
                  {it.agent_slug ? ` · ${agentLabel(it.agent_slug, agentsBySlug)}` : ""}
                </span>
              </td>
              <td className="hidden md:table-cell px-3 py-3.5 text-[12.5px] text-brand-ink2 font-ui whitespace-nowrap">{TYPE_LABEL[it.type] || it.type}</td>
              <td className="hidden sm:table-cell px-3 py-3.5 text-[12px] sm:text-[12.5px] text-brand-ink2 font-ui" data-testid={`library-agent-tag-${it.id}`}>
                <span className="line-clamp-2">{agentLabel(it.agent_slug, agentsBySlug)}</span>
              </td>
              <td className="hidden lg:table-cell px-3 py-3.5 text-[12.5px] font-ui">
                <span className={it.status === "draft" ? "text-brand-gold" : "text-brand-sage"}>
                  {STATUS_LABEL[it.status] || it.status}
                </span>
              </td>
              <td className="hidden sm:table-cell px-3 py-3.5 text-[12.5px] text-brand-ink2 font-ui whitespace-nowrap">
                {new Date(it.updated_at).toLocaleDateString("pt-BR")}
              </td>
              <td className="px-0 sm:px-5 py-3.5 text-right w-14 sm:w-auto" onClick={(e) => e.stopPropagation()}>
                <span className="md:hidden inline-flex justify-center w-full">
                  <ContinueBtn it={it} onContinue={onContinue} iconOnly />
                </span>
                <span className="hidden md:inline-flex">
                  <ContinueBtn it={it} onContinue={onContinue} compact />
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LibraryDetail({
  selected, agentsBySlug, viewer, pdfId, onExpand, onCopy, onPdf, onInstagram, onShare, onDelete, onContinue,
}) {
  return (
    <div className="bg-brand-surface border border-brand-line rounded-3xl p-5 sm:p-7" data-testid="library-detail">
      <div className="flex flex-col gap-3 mb-4">
        <div className="min-w-0">
          <AgentTypeLine it={selected} agentsBySlug={agentsBySlug} />
          <h2 className="font-display text-2xl lg:text-3xl text-brand-ink mt-1 break-words">
            {selected.title}
          </h2>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {selected.status === "draft" && <ContinueBtn it={selected} onContinue={onContinue} />}
          <button
            data-testid="library-expand"
            onClick={onExpand}
            className="p-2 rounded-full hover:bg-brand-elevated text-brand-ink2 hover:text-brand-rose"
            title="Tela cheia"
            aria-label="Tela cheia"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
          <button
            data-testid="library-detail-copy"
            onClick={onCopy}
            className="p-2 rounded-full hover:bg-brand-elevated text-brand-ink2 hover:text-brand-rose"
            title="Copiar"
            aria-label="Copiar"
          >
            <Copy className="w-4 h-4" />
          </button>
          {selected.status === "saved" && (
            <button
              data-testid="library-detail-pdf"
              onClick={onPdf}
              disabled={pdfId === selected.id}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-brand-elevated border border-brand-line text-[12.5px] font-ui text-brand-ink hover:border-brand-rose hover:text-brand-rose transition-colors min-h-[40px] disabled:opacity-50"
              title="Baixar PDF"
            >
              {pdfId === selected.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              PDF
            </button>
          )}
          {hasInstagramCaption(selected.content) && (
            <button
              data-testid="library-copy-instagram"
              onClick={onInstagram}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-brand-elevated border border-brand-line text-[12.5px] font-ui text-brand-ink hover:border-brand-rose hover:text-brand-rose transition-colors min-h-[40px]"
              title="Copiar legenda"
              aria-label="Copiar legenda"
            >
              <Instagram className="w-4 h-4" />
              <span className="hidden sm:inline">Instagram</span>
            </button>
          )}
          {canWebShare && (
            <button
              data-testid="library-share-instagram"
              onClick={onShare}
              className="p-2 rounded-full hover:bg-brand-elevated text-brand-ink2 hover:text-brand-rose"
              title="Compartilhar"
              aria-label="Compartilhar"
            >
              <Share2 className="w-4 h-4" />
            </button>
          )}
          {!viewer && (
            <button
              data-testid={`library-delete-${selected.id}`}
              onClick={onDelete}
              className="p-2 rounded-full hover:bg-brand-elevated text-brand-ink2 hover:text-brand-rose"
              title="Apagar"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <div className="-mx-5 sm:-mx-7 mb-4">
        <TablesBar artifact={selected} working={false} />
      </div>
      {selected.type === "carrossel" ? (
        <CarouselArtifact content={selected.content} />
      ) : (
        <Markdown text={selected.content} />
      )}
    </div>
  );
}
